import * as React from 'react';
import { AppShell as AppShellBase, ScrollArea, createStyles } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

// API
import { GetCurrentUser } from '../../api';

// Components
import { AppHeader } from '../AppHeader';
import { AppFooter } from '../AppFooter';
import { ScrollToTop } from '../ScrollToTop';
import { GetHelpButton } from '../GetHelpButton';

// Contexts
import { AuthContext, defaultAuthState, defaultFirebaseAuthState } from '../../contexts/Auth';
import { RefetchSelfProvider } from '../../contexts/RefetchSelf';

// Firebase
import { auth } from '../../firebase';

const { VITE_SENTRY_ENV } = import.meta.env;

const useStyles = createStyles((theme) => ({
    scrollArea: {
        paddingRight: '1rem',
        [theme.fn.smallerThan('sm')]: {
            paddingRight: '0'
        }
    },
    isUATBannerShowing: {
        [theme.fn.largerThan('sm')]: {
            paddingTop: '2rem !important'
        },
        [theme.fn.smallerThan('sm')]: {
            marginTop: '2rem !important'
        }
    }
}));

export const AppShell: React.FC<{}> = ({}) => {
    const matches = useMediaQuery('(min-width: 768px)');
    const { cx, classes } = useStyles();
    const { firebaseState, authState, setFirebaseState, setAuthState } = React.useContext(AuthContext);
    const navigate = useNavigate();
    const { data, refetch } = useQuery({
        queryKey: ['self'],
        queryFn: async () => {
            try {
                const response = await GetCurrentUser();

                return response;
            } catch (e: any) {
                console.log(e);
                Sentry.captureException(e);
                if ('statusCode' in e) {
                    navigate(`/error/${e.statusCode}`);
                }
            }
        },
        onSuccess: (data) => {
            setAuthState?.({ ...data, initializing: false });
        },
        onError: (data) => {
            setAuthState?.({ ...defaultAuthState, initializing: false });
        },
        enabled: false,
        refetchOnWindowFocus: false
    });

    React.useEffect(() => {
        const unsubscribeFirebaseAuthListener = auth.onAuthStateChanged((user) => {
            if (firebaseState?.user !== false && firebaseState?.user !== user && !user) {
                setAuthState?.({ ...defaultAuthState, initializing: false });
            }
            if (firebaseState?.user !== user) {
                if (user) {
                    setAuthState?.({ ...authState, initializing: true });
                    refetch();
                    Sentry.addBreadcrumb({
                        category: 'auth',
                        message: 'Firebase authenticated user ' + JSON.stringify(user),
                        level: 'info'
                    });

                    // Set Sentry user context
                    user &&
                        Sentry.setUser({
                            email: user?.email ?? '',
                            id: user?.uid,
                            ip_address: '{{auto}}'
                        });
                    setFirebaseState?.({ ...firebaseState, initializing: false, user });
                } else {
                    setFirebaseState?.({ ...defaultFirebaseAuthState, initializing: false });
                    setAuthState?.({ ...defaultAuthState, initializing: false });
                }
            }
        });

        return unsubscribeFirebaseAuthListener;
    }, []);

    return (
        <RefetchSelfProvider refetch={refetch}>
            <AppShellBase
                // header={<AppHeader />}
                // footer={<AppFooter />}
                styles={(theme) => ({
                    main: {
                        backgroundColor: theme.colors.gray[1],
                        // backgroundColor: '#fef8e8',
                        // minHeight: 'auto',
                        // paddingBottom: '5rem !important',
                        minHeight: '100vh',
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingRight: 0,
                        [theme.fn.smallerThan('sm')]: {
                            padding: 0
                        }
                    }
                })}
            >
                <ScrollArea.Autosize
                    mah={'100vh'}
                    mx={'auto'}
                    classNames={{
                        viewport: classes.scrollArea,
                        root: cx('', {
                            [classes.isUATBannerShowing]: VITE_SENTRY_ENV !== 'prd'
                        })
                    }}
                    offsetScrollbars={matches}
                >
                    <Outlet />
                    <ScrollToTop />
                </ScrollArea.Autosize>
            </AppShellBase>
        </RefetchSelfProvider>
    );
};
